import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials missing. Please check your .env file.');
}

const isMock = !supabaseUrl || supabaseUrl.includes('dummy.supabase.co') || !supabaseAnonKey || supabaseAnonKey.includes('dummykey');

let mockUser = null;
let authListener = null;
let channelListeners = [];

const inMemoryDB = {
  profiles: {
    'mock-123': {
      id: 'mock-123',
      full_name: 'Alex Johnson',
      username: 'alexj',
      created_at: '2023-10-15T10:00:00Z',
      friends_count: 6,
      bio: 'Software developer and tech enthusiast.',
      location: 'San Francisco, CA',
      avatar_url: null
    },
    'mock-2': {
      id: 'mock-2',
      full_name: 'Michael Rodriguez',
      username: 'mrodriguez',
      created_at: '2023-11-10T10:00:00Z',
      friends_count: 8,
      bio: 'Traveler and photographer',
      location: 'Los Angeles, CA',
      avatar_url: 'https://images.unsplash.com/photo-1724128195747-dd25cba7860f'
    },
    'mock-3': {
      id: 'mock-3',
      full_name: 'Sarah Chen',
      username: 'sarahc',
      created_at: '2024-01-05T10:00:00Z',
      friends_count: 12,
      bio: 'Digital marketing specialist',
      location: 'New York, NY',
      avatar_url: 'https://images.unsplash.com/photo-1507532459814-b32f63cf4497'
    }
  },
  posts: {
    'post_101': {
      id: 'post_101', 
      user_id: 'mock-2', 
      content: 'Inappropriate language in description of the mountain photo! I hate it so much you are the worst.', 
      created_at: new Date(Date.now() - 600000).toISOString(),
      likes: 0
    },
    'post_102': {
      id: 'post_102', 
      user_id: 'mock-3', 
      content: 'Spam link detected: go to http://spam-website.com to win free iphones!', 
      created_at: new Date(Date.now() - 1500000).toISOString(),
      likes: 2
    }
  },
  friend_requests: {},
  friends: {},
  uploads: {}
};

const createQueryBuilder = (tableName) => {
  let queryData = Object.values(inMemoryDB[tableName] || {});

  
  const builder = {
    select: () => builder,
    insert: (data) => {
      const items = Array.isArray(data) ? data : [data];
      items.forEach(item => {
        if (!item.id) item.id = 'id-' + Date.now() + Math.random();
        if (!item.created_at) item.created_at = new Date().toISOString();
        if (!inMemoryDB[tableName]) inMemoryDB[tableName] = {};
        inMemoryDB[tableName][item.id] = item;
        
        // Trigger mock realtime listeners
        channelListeners.forEach(cb => cb({ eventType: 'INSERT', new: item }));
      });
      queryData = items;
      return builder;
    },
    upsert: (data) => {
      const items = Array.isArray(data) ? data : [data];
      items.forEach(item => {
        if (!item.id) item.id = 'id-' + Date.now() + Math.random();
        if (!inMemoryDB[tableName]) inMemoryDB[tableName] = {};
        inMemoryDB[tableName][item.id] = { ...inMemoryDB[tableName][item.id], ...item };
      });
      queryData = items;
      return builder;
    },
    update: (data) => {
      queryData = queryData.map(item => {
        const updated = { ...item, ...data };
        inMemoryDB[tableName][item.id] = updated;
        return updated;
      });
      return builder;
    },
    delete: () => {
      queryData.forEach(item => {
        delete inMemoryDB[tableName][item.id];
      });
      queryData = [];
      return builder;
    },
    eq: (col, val) => {
      queryData = queryData.filter(item => item[col] === val);
      return builder;
    },
    neq: (col, val) => {
      queryData = queryData.filter(item => item[col] !== val);
      return builder;
    },
    in: () => builder,
    order: () => builder,
    limit: () => builder,
    range: () => builder,
    ilike: () => builder,
    like: () => builder,
    contains: () => builder,
    match: () => builder,
    textSearch: () => builder,
    or: () => builder,
    single: async () => ({ data: queryData[0] || {}, error: null }),
    maybeSingle: async () => ({ data: queryData[0] || null, error: null }),
    then: function(resolve) {
      let resultData = [...queryData];
      
      // Auto-join profiles for posts
      if (tableName === 'posts') {
        resultData = resultData.map(post => ({
          ...post,
          author: inMemoryDB.profiles[post.user_id] || { name: 'Unknown User', avatar: '' },
          comments: post.comments || [],
          likes: post.likes || 0
        }));
        
        // Sort by timestamp descending
        resultData.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      }
      
      if (tableName === 'friend_requests') {
        resultData = resultData.map(req => ({
          ...req,
          sender: inMemoryDB.profiles[req.sender_id]
        }));
      }

      if (tableName === 'friends') {
        resultData = resultData.map(f => ({
          ...f,
          friend: inMemoryDB.profiles[f.user_id_2]
        }));
      }
      
      resolve({ data: resultData, error: null });
    }
  };
  return builder;
};

const mockChannel = {
  on: (event, filter, cb) => {
    channelListeners.push(cb);
    return mockChannel;
  },
  subscribe: () => mockChannel,
  unsubscribe: () => {
    channelListeners = [];
  }
};

const mockSupabase = {
  auth: {
    getSession: async () => ({ data: { session: mockUser ? { user: mockUser } : null }, error: null }),
    onAuthStateChange: (cb) => {
      authListener = cb;
      cb(mockUser ? 'SIGNED_IN' : 'SIGNED_OUT', mockUser ? { user: mockUser } : null);
      return { data: { subscription: { unsubscribe: () => { authListener = null; } } } };
    },
    signUp: async (data) => {
      const id = 'mock-' + Date.now();
      const newUser = { id, email: data.email };
      mockUser = newUser;
      
      inMemoryDB.profiles[id] = {
        id,
        full_name: data.options?.data?.full_name || 'New User',
        username: data.options?.data?.username || data.email.split('@')[0],
        created_at: new Date().toISOString(),
        friends_count: 0,
        bio: 'Hello! I am new here.',
        location: 'Earth',
        avatar_url: null
      };

      if (authListener) authListener('SIGNED_IN', { user: newUser });
      return { data: { user: newUser }, error: null };
    },
    signInWithPassword: async (data) => {
      const user = { id: 'mock-123', email: data.email };
      mockUser = user;
      if (authListener) authListener('SIGNED_IN', { user });
      return { data: { user, session: { user } }, error: null };
    },
    signOut: async () => {
      mockUser = null;
      if (authListener) authListener('SIGNED_OUT', null);
      return { error: null };
    },
    updateUser: async (data) => {
      if (mockUser) {
        mockUser.user_metadata = { ...mockUser.user_metadata, ...data.data };
        if (inMemoryDB.profiles[mockUser.id]) {
          inMemoryDB.profiles[mockUser.id] = {
            ...inMemoryDB.profiles[mockUser.id],
            full_name: data.data.full_name || inMemoryDB.profiles[mockUser.id].full_name,
            username: data.data.username || inMemoryDB.profiles[mockUser.id].username,
            avatar_url: data.data.avatar_url || inMemoryDB.profiles[mockUser.id].avatar_url
          };
        }
        if (authListener) authListener('USER_UPDATED', { user: mockUser });
      }
      return { data: { user: mockUser }, error: null };
    }
  },
  from: (table) => createQueryBuilder(table),
  channel: () => mockChannel,
  rpc: async () => ({ data: [], error: null }),
  storage: {
    from: () => ({
      upload: async (path, file) => {
        const url = URL.createObjectURL(file);
        inMemoryDB.uploads[path] = url;
        return { data: { path }, error: null };
      },
      getPublicUrl: (path) => ({ data: { publicUrl: inMemoryDB.uploads[path] || 'https://via.placeholder.com/150' } }),
      remove: async () => ({ error: null }),
    })
  }
};

export const supabase = isMock ? mockSupabase : createClient(supabaseUrl, supabaseAnonKey);


